import React, { useMemo, useState } from "react";
import {
  Button,
  Drawer,
  Empty,
  Space,
  Table,
  Tag,
  Typography,
  Popconfirm,
  Tooltip,
  Image,
  Divider,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PictureOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

function buildTherapyMap(therapyCats) {
  const map = new Map();
  (therapyCats || []).forEach((t) => map.set(String(t._id), t));
  return map;
}

function renderTherapyTags(scene, therapyMap) {
  const items = scene?.module_07?.ModSevenTherapyIds || [];
  const normalized = items
    .map((x) => {
      if (!x) return null;
      if (typeof x === "string")
        return { _id: x, ...(therapyMap.get(String(x)) || {}) };
      if (typeof x === "object")
        return { ...x, ...(therapyMap.get(String(x._id)) || {}) };
      return null;
    })
    .filter(Boolean);

  return normalized.slice(0, 5).map((t) => (
    <Tag
      key={String(t._id)}
      style={
        t.colorCode
          ? { borderColor: t.colorCode, color: t.colorCode }
          : undefined
      }
    >
      {t.title || "Terapia"}
    </Tag>
  ));
}

export function ReviSceneList({
  scenes,
  therapyCats,
  loading,
  q,
  total,
  onPageChange,
  onDelete,
  onEdit,
}) {
  const therapyMap = useMemo(() => buildTherapyMap(therapyCats), [therapyCats]);
  const [drawer, setDrawer] = useState({ open: false, scene: null });

  const columns = [
    {
      title: "Sala",
      key: "title",
      width: 350,
      render: (_, scene) => {
        const title = scene?.module_01?.ModOneTitle || "(Sin título)";
        const url = scene?.module_01?.ModOneUrlsala;

        const bannerObj = scene?.module_01?.bannertop;
        let bannerUrl = null;
        if (bannerObj && typeof bannerObj === "object" && bannerObj.url) {
          bannerUrl = bannerObj.url;
        }

        const id = scene?._id;

        // Sala del catálogo Educa360 a la que REVI añadió su bloque clínico:
        // 110 de las 161 del catálogo. Conviene verlo antes de editar, porque
        // título y URL se comparten con la otra vertical.
        const compartida =
          String(scene?.module_00?.departament || "").toLowerCase() !== "revi";

        return (
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div
              style={{
                width: 80,
                height: 50,
                borderRadius: 6,
                overflow: "hidden",
                flexShrink: 0,
                background: "#f5f5f5",
                border: "1px solid #e0e0e0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {bannerUrl ? (
                <Image
                  src={bannerUrl}
                  width={80}
                  height={50}
                  style={{ objectFit: "cover" }}
                  fallback="https://via.placeholder.com/80x50?text=Error"
                />
              ) : (
                <Tooltip title="Imagen no encontrada o no populada">
                  <PictureOutlined style={{ color: "#bdbdbd", fontSize: 20 }} />
                </Tooltip>
              )}
            </div>

            <Space direction="vertical" size={2} style={{ maxWidth: 220 }}>
              <Text strong style={{ lineHeight: 1.2 }}>
                {title}
              </Text>
              {compartida ? (
                <Tooltip title="Sala del catálogo Educa360 compartida con REVI. Título y URL se aplican en las dos verticales.">
                  <Tag color="geekblue" style={{ fontSize: 10, margin: 0 }}>
                    Educa360
                  </Tag>
                </Tooltip>
              ) : null}
              <Text type="secondary" style={{ fontSize: 10 }} copyable>
                {id}
              </Text>
              {url ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: 11 }}
                >
                  Abrir en Hubs
                </a>
              ) : null}
            </Space>
          </div>
        );
      },
    },
    {
      title: "Terapias",
      key: "therapies",
      render: (_, scene) => (
        <Space wrap size={[0, 4]}>
          {renderTherapyTags(scene, therapyMap)}
        </Space>
      ),
    },
    {
      title: "Dificultad",
      key: "difficulty",
      width: 110,
      render: (_, scene) => (
        <Tag>{scene?.module_07?.ModSevenDifficultyLevel || "—"}</Tag>
      ),
    },
    {
      title: "Premium",
      key: "premium",
      width: 100,
      render: (_, scene) =>
        scene?.module_07?.ReviScenePremium ? (
          <Tag color="gold">PRO</Tag>
        ) : (
          <Tag>Free</Tag>
        ),
    },
    {
      title: "Activo",
      key: "active",
      width: 90,
      render: (_, scene) => {
        const v = scene?.module_07?.ModSevenActive;
        return v ? <Tag color="green">Sí</Tag> : <Tag color="red">No</Tag>;
      },
    },
    {
      title: "Acciones",
      key: "actions",
      width: 140,
      fixed: "right",
      render: (_, scene) => {
        const isPremium = scene?.module_07?.ReviScenePremium;
        return (
          <Space>
            <Tooltip title="Ver detalles clínicos">
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => setDrawer({ open: true, scene })}
              />
            </Tooltip>

            <Tooltip title="Editar">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => onEdit && onEdit(scene)}
              />
            </Tooltip>

            <Popconfirm
              title={isPremium ? "Borrar Sala Completa" : "Desvincular de REVI"}
              description={
                isPremium
                  ? "Se eliminarán todos los archivos y datos."
                  : "Solo se desvinculará de Revi."
              }
              onConfirm={() => onDelete && onDelete(scene._id)}
              okText="Sí, borrar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={scenes}
        loading={loading}
        locale={{ emptyText: <Empty description="No se encontraron salas" /> }}
        pagination={{
          current: q.page,
          pageSize: q.limit,
          total,
          showSizeChanger: true,
          onChange: (page, pageSize) => onPageChange(page, pageSize),
        }}
        scroll={{ x: 1100 }}
      />

      <Drawer
        title="Ficha Clínica"
        open={drawer.open}
        onClose={() => setDrawer({ open: false, scene: null })}
        width={420}
      >
        {drawer.scene ? (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Text strong>Descripción:</Text>
            <Text style={{ whiteSpace: "pre-wrap" }}>
              {drawer.scene.module_07?.ModSevenClinicalDescription}
            </Text>
            <Divider />
            <Text strong>Guía:</Text>
            <Text style={{ whiteSpace: "pre-wrap" }}>
              {drawer.scene.module_07?.ModSevenInterventionGuide}
            </Text>
          </Space>
        ) : null}
      </Drawer>
    </>
  );
}
